import { AssignPlanMutator } from '@/lib/entity/assign_plan'
import { createKey } from '@/lib/stageUtil'
import { useStageStore } from '@/stores/stage'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { differenceInDays, parseISO } from 'date-fns'
import { VolumedAssignPlanMutator } from '@/lib/entity/volumed_assign_plan'
import { nanoid } from 'nanoid'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'

type StageStorage = ReturnType<typeof useStageStore>
type AssignPlanKind = 'assign_plan' | 'volumed_assign_plan'

export const copyStyles = {
  copied: 'border-2 border-dashed border-blue-500 hover:bg-blue-200',
  selected: 'border-2 border-blue-500 bg-blue-50',
  selectable: 'hover:bg-blue-100',
  rowCopied: 'bg-gray-100'
} as const

/**
 * アサインのコピー&ペースト機能を提供する公開用 composable。
 */
export const useAssignClipboard = () => {
  const selection = useAssignSelection()
  const clipboardService = useClipboardService()
  const deselector = useDeselect()

  const handleClipboardKey = (e: KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return

    switch (e.key.toLowerCase()) {
      case 'c':
        clipboardService.copy()
        break
      case 'x':
        clipboardService.cut()
        break
      case 'v':
        clipboardService.paste()
        break
    }
  }

  return {
    select: selection.select,
    deselect: selection.deselect,
    isCopySource: ({ assignId }: { assignId: string }) => selection.isLastCopiedAssignId(assignId),
    isSelected: ({ assignId }: { assignId: string }) => selection.isSelected(assignId),
    resetState: () => deselector.deselect(),
    handleClipboardKey
  }
}

function useClipboardService() {
  const stage = useStageStore()
  const selectionStore = useSelectedAssignStore()
  const lastCopiedStore = useLastCopiedAssignStore()
  const copiedDataStore = useCopiedDataStore()
  const lastSelectedStore = useLastSelectedAssignStore()

  // NOTE:
  // storeToRefs を使わずに直接分割代入すると、
  // その時点の値がコピーされリアクティビティが失われる
  const { selected } = storeToRefs(selectionStore)
  const { copiedData } = storeToRefs(copiedDataStore)
  const { lastSelectedAssign } = storeToRefs(lastSelectedStore)
  const { lastCopiedAssignId } = storeToRefs(lastCopiedStore)

  return new StageClipboardService(
    stage,
    selected,
    copiedData,
    lastSelectedAssign,
    lastCopiedAssignId
  )
}

/**
 * UI のクリック操作で selection/clipboard 関連ストアを一貫して更新する。
 */
function useAssignSelection() {
  const selectionStore = useSelectedAssignStore()
  const lastCopiedStore = useLastCopiedAssignStore()
  const lastSelectedStore = useLastSelectedAssignStore()

  return {
    select(params: {
      kind: AssignPlanKind
      projectId: string
      assignId: string
      assignPlans: ClipboardAssignPlan[]
    }) {
      selectionStore.select({
        assignId: params.assignId,
        assignPlans: params.assignPlans
      })
      lastSelectedStore.setLastSelectedAssign({
        kind: params.kind,
        selectedAssignId: params.assignId,
        projectId: params.projectId
      })
    },
    deselect(assignId: string) {
      if (selectionStore.isSelected(assignId)) {
        selectionStore.deselectAll()
        lastSelectedStore.setLastSelectedAssign(undefined)
      }
    },
    isLastCopiedAssignId: lastCopiedStore.isLastCopiedAssignId,
    isSelected: selectionStore.isSelected
  }
}

/**
 * ESC 操作などで選択状態をまとめてリセットするためのストア。
 * selection/lastCopied/lastSelected を横断的に初期化する責務を持つ。
 */
const useDeselect = defineStore('deselect', () => {
  const selectionStore = useSelectedAssignStore()
  const lastCopiedStore = useLastCopiedAssignStore()
  const lastSelectedStore = useLastSelectedAssignStore()
  const copiedDataStore = useCopiedDataStore()

  return {
    deselect() {
      selectionStore.deselectAll()
      lastCopiedStore.setLastCopiedAssignId(undefined)
      lastSelectedStore.setLastSelectedAssign(undefined)
      copiedDataStore.setCopiedData(undefined)
    }
  }
})

/**
 * 選択中のアサインの状態（ID・プラン群）を一元管理するストア。
 */
const useSelectedAssignStore = defineStore('selectedAssignStore', () => {
  interface SelectState {
    assignId: string
    assignPlans: ClipboardAssignPlan[]
  }
  const selectState = ref<SelectState | null>(null)

  const selected = computed(() => selectState.value?.assignPlans ?? [])

  return {
    selected,
    select(params: SelectState) {
      selectState.value = params
    },
    deselectAll() {
      selectState.value = null
    },
    isSelected(assignId: string) {
      return selectState.value?.assignId === assignId
    },
    toggleSelect(params: SelectState) {
      if (this.isSelected(params.assignId)) {
        this.deselectAll()
      } else {
        this.select(params)
      }
    }
  }
})

/**
 * 直近でコピーしたアサインIDを保持するストア（ハイライト用）。
 */
const useLastCopiedAssignStore = defineStore('lastCopiedAssignStore', () => {
  const lastCopiedAssignId = ref<string | undefined>()

  return {
    lastCopiedAssignId,
    isLastCopiedAssignId(assignId: string) {
      return lastCopiedAssignId.value === assignId
    },
    setLastCopiedAssignId(assignId: string | undefined) {
      lastCopiedAssignId.value = assignId
    }
  }
})

/**
 * コピーしたAssignPlanデータを保持するストア。
 */
const useCopiedDataStore = defineStore('copiedDataStore', () => {
  const copiedData = ref<ClipboardAssignPlan[] | undefined>(undefined)

  return {
    copiedData,
    setCopiedData(data: ClipboardAssignPlan[] | undefined) {
      copiedData.value = data
    }
  }
})

/**
 * 直近で選択されたアサイン情報（ペースト対象）を保持するストア。
 */
const useLastSelectedAssignStore = defineStore('lastSelectedAssignStore', () => {
  const lastSelectedAssign = ref<StageClipboardPasteParams | undefined>(undefined)

  return {
    lastSelectedAssign,
    setLastSelectedAssign(params: StageClipboardPasteParams | undefined) {
      lastSelectedAssign.value = params
    }
  }
})

interface StageClipboardPasteParams {
  selectedAssignId: string
  projectId: string
  kind: AssignPlanKind
}

// ============================================================================
// StageClipboardService
// ============================================================================

/**
 * copy/cut/paste の実処理を担うサービスクラス。
 * Ref を直接受け取り、.value でアクセスすることで常に最新値を参照する。
 */
class StageClipboardService {
  constructor(
    private readonly stage: StageStorage,
    private readonly selected: ComputedRef<ClipboardAssignPlan[]>,
    private readonly copiedData: Ref<ClipboardAssignPlan[] | undefined>,
    private readonly lastSelectedAssign: Ref<StageClipboardPasteParams | undefined>,
    private readonly lastCopiedAssignId: Ref<string | undefined>
  ) {}

  copy() {
    this.copiedData.value = [...this.selected.value]
    this.lastCopiedAssignId.value = this.lastSelectedAssign.value?.selectedAssignId
  }

  async cut() {
    this.copy()

    const lastSelectedInfo = this.lastSelectedAssign.value
    if (lastSelectedInfo === undefined) return

    const { selectedAssignId: targetAssignId, kind } = lastSelectedInfo
    await this.deleteAssignPlanByAssignId(kind, targetAssignId)
  }

  async paste() {
    const lastSelectedInfo = this.lastSelectedAssign.value
    if (lastSelectedInfo === undefined) return

    const { selectedAssignId: targetAssignId, projectId, kind } = lastSelectedInfo

    const copiedPlans = this.copiedData.value
    if (copiedPlans === undefined || copiedPlans.length === 0) return

    const projectPeriod = await getProjectPeriod(projectId)
    if (projectPeriod === undefined) return

    const offsetPlans = toClippedOffsetPlans(
      copiedPlans,
      projectPeriod.absoluteStartDate,
      projectPeriod.absoluteEndDate
    )

    await this.deleteAssignPlanByAssignId(kind, targetAssignId)
    this.writeAssignPlans(kind, targetAssignId, offsetPlans)
  }

  /**
   * SQLiteにあるPlanとstageStorageにあるPlanを削除する
   */
  private async deleteAssignPlanByAssignId(kind: AssignPlanKind, assignId: string) {
    if (kind === 'assign_plan') {
      const mutators = await AssignPlanMutator.listByAssignId(assignId)
      for (const m of mutators) {
        if (m.base === undefined) continue
        const key = createKey('assign_plan', m.base.id)
        const cm = this.stage.add(key, m)
        cm.delete()
      }
    } else if (kind === 'volumed_assign_plan') {
      const mutators = await VolumedAssignPlanMutator.listByAssignId(assignId)
      for (const m of mutators) {
        if (m.base === undefined) continue
        const key = createKey('volumed_assign_plan', m.base.id)
        const cm = this.stage.add(key, m)
        cm.delete()
      }
    }

    // writeAssignPlansで作られたidを把握して、それを削除する処理の方が
    // パフォーマンス的には優れているが(O(N)からO(1)になる)、状態を追加したくないのでstageに依存する
    this.stage.getAll().forEach((v) => {
      if (kind === v.table && v.table === 'assign_plan' && v.context.assign_id === assignId) {
        v.delete()
      } else if (
        kind === v.table &&
        v.table === 'volumed_assign_plan' &&
        v.context.volumed_assign_id === assignId
      ) {
        v.delete()
      }
    })
  }

  private writeAssignPlans(kind: AssignPlanKind, assignId: string, newData: OffsetPlan[]) {
    const plans = newData.map((plan) => ({
      start_offset: plan.startOffset,
      end_offset: plan.endOffset,
      estimate: plan.estimateHours / 3600
    }))
    const mutators = plans.map((plan) => {
      const id = nanoid()
      if (kind === 'assign_plan') {
        const instance = AssignPlanMutator.createInstance()
        instance.context = { id, assign_id: assignId }
        Object.assign(instance.modified, { ...plan, assign_id: assignId })
        const key = createKey('assign_plan', id)
        return { instance, key }
      } else {
        const instance = VolumedAssignPlanMutator.createInstance()
        instance.context = { id, volumed_assign_id: assignId }
        Object.assign(instance.modified, { ...plan, volumed_assign_id: assignId })
        const key = createKey('volumed_assign_plan', id)
        return { instance, key }
      }
    })
    mutators.forEach(({ instance, key }) => {
      this.stage.add(key, instance)
    })
  }
}

/**
 * プロジェクトの期間を取得する
 */
async function getProjectPeriod(
  projectId: string
): Promise<{ absoluteStartDate: Date; absoluteEndDate: Date } | undefined> {
  const results = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom('project')
      .select(['start_date', 'end_date'])
      .where('id', '=', projectId)
      .execute()
  })
  const result = results[0]
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (!result) return undefined
  return {
    absoluteStartDate: parseISO(result.start_date),
    absoluteEndDate: parseISO(result.end_date)
  }
}

type OffsetPlan = {
  id: string
  startOffset: number
  endOffset: number
  estimateHours: number
}

function toClippedOffsetPlans(
  plans: ClipboardAssignPlan[],
  targetAbsoluteStartDate: Date,
  targetAbsoluteEndDate: Date
): OffsetPlan[] {
  const maxOffset = differenceInDays(targetAbsoluteEndDate, targetAbsoluteStartDate)

  return plans
    .map((plan) => {
      const startOffset = differenceInDays(plan.absoluteStartDate, targetAbsoluteStartDate)
      const endOffset = differenceInDays(plan.absoluteEndDate, targetAbsoluteStartDate)

      const clippedStartOffset = Math.max(0, startOffset)
      const clippedEndOffset = Math.min(endOffset, maxOffset)

      return {
        id: plan.id,
        startOffset: clippedStartOffset,
        endOffset: clippedEndOffset,
        estimateHours: plan.estimateHours
      }
    })
    .filter(
      (plan) =>
        0 <= plan.startOffset && plan.endOffset <= maxOffset && plan.startOffset <= plan.endOffset
    )
}

type ClipboardAssignPlan = {
  id: string
  absoluteStartDate: Date
  absoluteEndDate: Date
  estimateHours: number
}

/**
 * LayeredなPlanをClipboardAssignPlanに変換する。
 * computedの中で呼び出すことでリアクティブに動作する。
 */
interface LayeredPlan {
  id: { merged: () => string }
  start: { merged: () => string }
  end: { merged: () => string }
  estimate: { merged: () => number }
}

export function toClipboardPlans(plans: LayeredPlan[]): ClipboardAssignPlan[] {
  return plans.map((plan) => ({
    id: plan.id.merged(),
    absoluteStartDate: parseISO(plan.start.merged()),
    absoluteEndDate: parseISO(plan.end.merged()),
    estimateHours: plan.estimate.merged()
  }))
}
